/**
 * 3DS iframe modal for displaying authentication pages.
 *
 * This module provides an iframe modal used by payment processors
 * that need to display a 3DS authentication page.
 */

/** Shared overlay/shadow colors so other modals mirroring this chrome don't redefine the literals. */
export const MODAL_OVERLAY_SCRIM = "rgba(0, 0, 0, 0.5)";
export const MODAL_SHADOW = "0 12px 48px rgba(0, 0, 0, 0.15)";
export const MODAL_CLOSE_BUTTON_SHADOW = "0 2px 8px rgba(0, 0, 0, 0.15)";

type IframeModalResult = { success: true; data: Record<string, unknown> } | { success: false; error: string };

type ThreeDSCompleteMessage = {
  type: "3ds_complete";
  processor: string | null;
  isSuccess: boolean;
  error?: string;
};

type IframeModalMessageHandler = (
  event: MessageEvent<ThreeDSCompleteMessage>,
) => { handled: true; result: IframeModalResult } | { handled: false };

interface IframeModalOptions {
  /** URL to load in the iframe (via `src`). Optional when `srcdocHtml` is given. */
  url?: string;
  /**
   * Raw ACS HTML to render in the iframe (via `srcdoc`). Used by VGS 3DS, whose
   * challenge_form is HTML — NOT a URL — so the two are not interchangeable. Takes
   * precedence over `url` when both are set (defensive; VGS never sets both).
   */
  srcdocHtml?: string;
  /** Size of the iframe in pixels */
  size: { width: number; height: number };
  /** Border radius for the iframe in pixels (default: 16) */
  radius?: number;
  /** Handler to process postMessage events from the iframe */
  onMessage: IframeModalMessageHandler;
}

/**
 * Shows an iframe modal and waits for a result via postMessage.
 *
 * Features:
 * - Close/cancel button positioned outside the iframe
 * - Responsive design (works on mobile)
 * - Keyboard accessibility (Escape to close, focus trap)
 * - Fade-in/fade-out animations
 * - Iframe load error handling
 *
 * @param options - Modal configuration options
 * @returns Promise that resolves with success/failure result
 *
 * @example
 * ```ts
 * const result = await show3DSIframeModal({
 *   url: "https://bank.com/3ds-auth",
 *   size: { width: 605, height: 500 },
 *   radius: 6,
 *   onMessage: (event) => {
 *     if (event.data?.type === "auth_complete") {
 *       return {
 *         handled: true,
 *         result: event.data.succeeded
 *           ? { success: true, data: { id: event.data.id } }
 *           : { success: false, error: event.data.error }
 *       };
 *     }
 *     return { handled: false };
 *   },
 * });
 * ```
 */

export default function show3DSIframeModal(options: IframeModalOptions): Promise<IframeModalResult> {
  const { url, srcdocHtml, size, radius = 6, onMessage } = options;
  if (!url && !srcdocHtml) {
    throw new Error("show3DSIframeModal requires either `url` or `srcdocHtml`");
  }

  return new Promise((resolve) => {
    let resolved = false;

    // Create modal overlay with fade-in animation
    const overlay = document.createElement("div");
    overlay.id = "pk-iframe-modal-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", "3D Secure Verification");
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      transition: background 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    `;

    // Create container for iframe and close button
    const container = document.createElement("div");
    // Give the container its own card background (white) so the close button and the
    // iframe always sit on a solid surface — during iframe load, a blank callback
    // page, or any transparent ACS content — instead of floating over the overlay.
    container.style.cssText = `
      position: relative;
      width: ${size.width}px;
      height: ${size.height}px;
      max-width: calc(100vw - 32px);
      max-height: calc(100vh - 64px);
      overflow: visible;
      background: white;
      border-radius: ${radius}px;
      box-shadow: ${MODAL_SHADOW};
      opacity: 0;
      transform: scale(0.95) translateY(10px);
      transition: opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    `;

    // Create close button (positioned inside top-right of container)
    // Uses !important to prevent merchant CSS from hiding/repositioning the button
    const closeButton = document.createElement("button");
    closeButton.id = "pk-iframe-modal-close";
    closeButton.setAttribute("aria-label", "Cancel verification");
    closeButton.setAttribute("type", "button");
    closeButton.style.cssText = `
      position: absolute !important;
      top: 8px !important;
      right: 8px !important;
      width: 32px !important;
      height: 32px !important;
      border: none !important;
      background: white !important;
      border-radius: 8px !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      z-index: 10 !important;
      transition: background 0.15s ease !important;
      box-shadow: ${MODAL_CLOSE_BUTTON_SHADOW} !important;
    `;
    closeButton.innerHTML = `
      <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1L13 13M1 13L13 1" stroke="#666" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `;
    closeButton.onmouseenter = (): void => {
      closeButton.style.setProperty("background", "#f5f5f5", "important");
    };
    closeButton.onmouseleave = (): void => {
      closeButton.style.setProperty("background", "white", "important");
    };

    // Create iframe (hidden initially until loaded).
    // srcdocHtml (raw ACS HTML) takes precedence over url; challenge_form is HTML,
    // not a navigable URL.
    const iframe = document.createElement("iframe");
    if (srcdocHtml) {
      iframe.srcdoc = srcdocHtml;
    } else if (url) {
      iframe.src = url;
    }
    iframe.setAttribute("title", "3D Secure Verification");
    iframe.style.cssText = `
      width: 100%;
      height: 100%;
      border: none;
      opacity: 0;
      border-radius: ${radius}px;
      background: white;
      transition: opacity 0.2s ease;
      position: relative;
      z-index: 1;
    `;

    // Handle iframe load success
    iframe.onload = (): void => {
      iframe.style.opacity = "1";
    };

    // Handle iframe load error
    iframe.onerror = (): void => {
      if (!resolved) {
        console.error("[IframeModal] Failed to load iframe");
        cleanupWithAnimation();
        resolve({ success: false, error: "Failed to load verification page. Please try again." });
      }
    };

    container.appendChild(closeButton);
    container.appendChild(iframe);
    overlay.appendChild(container);
    document.body.appendChild(overlay);

    // Trigger fade-in animation
    requestAnimationFrame(() => {
      overlay.style.background = MODAL_OVERLAY_SCRIM;
      container.style.opacity = "1";
      container.style.transform = "scale(1) translateY(0)";
    });

    // Cleanup with fade-out animation
    const cleanupWithAnimation = (): void => {
      if (resolved) return;
      resolved = true;

      window.removeEventListener("message", handleMessage);
      window.removeEventListener("keydown", handleKeydown);

      // Use requestAnimationFrame to ensure transition is applied
      requestAnimationFrame(() => {
        overlay.style.background = "rgba(0, 0, 0, 0)";
        container.style.opacity = "0";
        container.style.transform = "scale(0.95) translateY(10px)";

        setTimeout(() => {
          overlay.remove();
        }, 250);
      });
    };

    // Handle close button click
    const handleCancel = (): void => {
      if (!resolved) {
        console.log("[IframeModal] User cancelled");
        cleanupWithAnimation();
        resolve({ success: false, error: "Verification cancelled" });
      }
    };

    closeButton.onclick = handleCancel;

    // Handle Escape key
    const handleKeydown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        handleCancel();
      }
      // Trap focus within modal (Tab key cycles through close button and iframe)
      if (event.key === "Tab") {
        event.preventDefault();
        if (document.activeElement === closeButton) {
          iframe.focus();
        } else {
          closeButton.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeydown);

    // Set initial focus to close button for accessibility
    closeButton.focus();

    const handleMessage = (event: MessageEvent): void => {
      const messageResult = onMessage(event);
      if (messageResult.handled) {
        cleanupWithAnimation();
        resolve(messageResult.result);
      }
    };

    window.addEventListener("message", handleMessage);
  });
}
